import { EnhancedSearchConsoleData } from '../types';

export interface Insight {
  type: string;
  title: string;
  description: string;
  query: string;
  page?: string;
  position: number;
  clicks: number;
  impressions: number;
  ctr: number;
  action: string;
  severity: 'high' | 'medium' | 'low';
  conflictingPages?: string[]; // For cannibalization insights
}

export interface InsightsSummary {
  quickWins: Insight[];
  strikingDistance: Insight[];
  ctrAnomalies: Insight[];
  featuredSnippets: Insight[];
  cannibalization: Insight[];
  longTail: Insight[];
  localIntent: Insight[];
  total: number;
}

// Calculate CTR benchmarks by position ranges
const calculateCTRBenchmarks = (data: EnhancedSearchConsoleData[]) => {
  const positionBuckets = {
    top3: data.filter(row => row.position >= 1 && row.position <= 3),
    top10: data.filter(row => row.position >= 4 && row.position <= 10),
    top20: data.filter(row => row.position >= 11 && row.position <= 20),
    beyond20: data.filter(row => row.position > 20)
  };

  const getPercentile = (values: number[], percentile: number) => {
    const sorted = values.sort((a, b) => a - b);
    const index = Math.ceil((percentile / 100) * sorted.length) - 1;
    return sorted[index] || 0;
  };

  // Fallback CTR benchmarks for sparse data
  const fallbacks = {
    top3: { median: 0.25, p25: 0.20 },
    top10: { median: 0.12, p25: 0.08 },
    top20: { median: 0.06, p25: 0.04 }
  };

  return {
    top3: {
      median: positionBuckets.top3.length >= 10 ? getPercentile(positionBuckets.top3.map(r => r.ctr), 50) : fallbacks.top3.median,
      p25: positionBuckets.top3.length >= 10 ? getPercentile(positionBuckets.top3.map(r => r.ctr), 25) : fallbacks.top3.p25
    },
    top10: {
      median: positionBuckets.top10.length >= 10 ? getPercentile(positionBuckets.top10.map(r => r.ctr), 50) : fallbacks.top10.median,
      p25: positionBuckets.top10.length >= 10 ? getPercentile(positionBuckets.top10.map(r => r.ctr), 25) : fallbacks.top10.p25
    },
    top20: {
      median: positionBuckets.top20.length >= 10 ? getPercentile(positionBuckets.top20.map(r => r.ctr), 50) : fallbacks.top20.median,
      p25: positionBuckets.top20.length >= 10 ? getPercentile(positionBuckets.top20.map(r => r.ctr), 25) : fallbacks.top20.p25
    }
  };
};

const getImpressionThresholds = (data: EnhancedSearchConsoleData[]) => {
  const impressions = data.map(row => row.impressions).sort((a, b) => a - b);
  const p50 = impressions[Math.floor(impressions.length * 0.5)] || 100;
  const p75 = impressions[Math.floor(impressions.length * 0.75)] || 200;
  
  return { p50: Math.max(p50, 50), p75: Math.max(p75, 100) };
};

export const analyzeInsights = (data: EnhancedSearchConsoleData[]): InsightsSummary => {
  if (!data || data.length === 0) {
    return {
      quickWins: [],
      strikingDistance: [],
      ctrAnomalies: [],
      featuredSnippets: [],
      cannibalization: [],
      longTail: [],
      localIntent: [],
      total: 0
    };
  }

  const ctrBenchmarks = calculateCTRBenchmarks(data);
  const { p50, p75 } = getImpressionThresholds(data);
  const insights: InsightsSummary = {
    quickWins: [],
    strikingDistance: [],
    ctrAnomalies: [],
    featuredSnippets: [],
    cannibalization: [],
    longTail: [],
    localIntent: [],
    total: 0
  };

  // Track query-page combinations for cannibalization detection
  const queryPageMap = new Map<string, EnhancedSearchConsoleData[]>();
  data.forEach(row => {
    if (!row.query) return;
    const key = row.query.toLowerCase();
    if (!queryPageMap.has(key)) {
      queryPageMap.set(key, []);
    }
    queryPageMap.get(key)!.push(row);
  });

  data.forEach(row => {
    // Quick Wins: Position 4-10, high impressions, low CTR
    if (row.position >= 4 && row.position <= 10 && 
        row.impressions >= p75 && 
        row.ctr < (ctrBenchmarks.top10.median * 0.8)) {
      insights.quickWins.push({
        type: 'quick-win',
        title: 'Quick Win Opportunity',
        description: `Ranking in position ${Math.round(row.position)} with ${row.impressions} impressions but only ${(row.ctr * 100).toFixed(1)}% CTR`,
        query: row.query,
        page: row.page,
        position: row.position,
        clicks: row.clicks,
        impressions: row.impressions,
        ctr: row.ctr,
        action: 'Improve title/meta description, add rich snippets',
        severity: 'high'
      });
    }

    // Striking Distance: Position 11-20, good impressions
    if (row.position >= 11 && row.position <= 20 && row.impressions >= p75) {
      insights.strikingDistance.push({
        type: 'striking-distance',
        title: 'Striking Distance',
        description: `Position ${Math.round(row.position)} with ${row.impressions} impressions - close to page 1`,
        query: row.query,
        page: row.page,
        position: row.position,
        clicks: row.clicks,
        impressions: row.impressions,
        ctr: row.ctr,
        action: 'On-page optimization + internal linking',
        severity: 'medium'
      });
    }

    // CTR Anomalies: Top 3 positions with very low CTR
    if (row.position >= 1 && row.position <= 3 && 
        row.ctr < ctrBenchmarks.top3.p25 && 
        row.impressions >= p50) {
      insights.ctrAnomalies.push({
        type: 'ctr-anomaly',
        title: 'Low CTR for Top Position',
        description: `Position ${Math.round(row.position)} but only ${(row.ctr * 100).toFixed(1)}% CTR`,
        query: row.query,
        page: row.page,
        position: row.position,
        clicks: row.clicks,
        impressions: row.impressions,
        ctr: row.ctr,
        action: 'Test new titles/descriptions, check search intent',
        severity: 'high'
      });
    }

    // Featured Snippet Opportunities: Question queries in positions 2-10
    const questionWords = ['who', 'what', 'why', 'how', 'when', 'where', 'which'];
    const isQuestion = row.query && questionWords.some(word => 
      row.query.toLowerCase().startsWith(word + ' ') || 
      row.query.toLowerCase().includes(' ' + word + ' ')
    );
    
    if (isQuestion && row.position >= 2 && row.position <= 10 && row.impressions >= p50) {
      insights.featuredSnippets.push({
        type: 'featured-snippet',
        title: 'Featured Snippet Opportunity',
        description: `Question query at position ${Math.round(row.position)}`,
        query: row.query,
        page: row.page,
        position: row.position,
        clicks: row.clicks,
        impressions: row.impressions,
        ctr: row.ctr,
        action: 'Add structured answers, FAQs, schema markup',
        severity: 'medium'
      });
    }

    // Long-tail Opportunities: 4+ words, impressions but no clicks
    const wordCount = row.query.trim().split(/\s+/).length;
    if (wordCount >= 4 && row.impressions >= p50 && row.clicks === 0) {
      insights.longTail.push({
        type: 'long-tail',
        title: 'Long-tail Opportunity',
        description: `${wordCount}-word query with ${row.impressions} impressions but no clicks`,
        query: row.query,
        page: row.page,
        position: row.position,
        clicks: row.clicks,
        impressions: row.impressions,
        ctr: row.ctr,
        action: 'Create targeted content page',
        severity: 'low'
      });
    }

    // Local Intent: Geographic queries
    const localKeywords = ['near me', 'near', 'in ', 'london', 'uk', 'england', 'manchester', 'birmingham', 'leeds', 'liverpool', 'bristol'];
    const hasLocalIntent = row.query && localKeywords.some(keyword => 
      row.query.toLowerCase().includes(keyword)
    );
    
    if (hasLocalIntent && row.position >= 5 && row.position <= 20 && row.impressions >= p50) {
      insights.localIntent.push({
        type: 'local-intent',
        title: 'Local SEO Opportunity',
        description: `Local query at position ${Math.round(row.position)}`,
        query: row.query,
        page: row.page,
        position: row.position,
        clicks: row.clicks,
        impressions: row.impressions,
        ctr: row.ctr,
        action: 'Create/optimize local pages, add LocalBusiness schema',
        severity: 'medium'
      });
    }
  });

  // Cannibalization Detection: Same query ranking for multiple pages
  queryPageMap.forEach((pages, query) => {
    if (pages.length > 1 && pages.some(p => p.impressions >= p50)) {
      const sortedPages = pages.sort((a, b) => b.clicks - a.clicks);
      const topPage = sortedPages[0];
      const allPages = sortedPages.map(p => p.page || '').filter(Boolean);
      
      insights.cannibalization.push({
        type: 'cannibalization',
        title: 'Keyword Cannibalization',
        description: `"${query}" ranks for ${pages.length} different pages`,
        query: query,
        page: topPage.page,
        position: topPage.position,
        clicks: topPage.clicks,
        impressions: topPage.impressions,
        ctr: topPage.ctr,
        action: 'Consolidate content or set canonical URLs',
        severity: 'high',
        conflictingPages: allPages
      });
    }
  });

  // Sort each category by severity and impact (impressions * position factor)
  const sortBySeverity = (a: Insight, b: Insight) => {
    const severityWeight = { high: 3, medium: 2, low: 1 };
    const aScore = severityWeight[a.severity] * a.impressions / Math.max(a.position, 1);
    const bScore = severityWeight[b.severity] * b.impressions / Math.max(b.position, 1);
    return bScore - aScore;
  };

  insights.quickWins.sort(sortBySeverity);
  insights.strikingDistance.sort(sortBySeverity);
  insights.ctrAnomalies.sort(sortBySeverity);
  insights.featuredSnippets.sort(sortBySeverity);
  insights.cannibalization.sort(sortBySeverity);
  insights.longTail.sort(sortBySeverity);
  insights.localIntent.sort(sortBySeverity);

  // Limit each category to top 10 items for performance
  const limitResults = (items: Insight[]) => items.slice(0, 10);
  
  insights.quickWins = limitResults(insights.quickWins);
  insights.strikingDistance = limitResults(insights.strikingDistance);
  insights.ctrAnomalies = limitResults(insights.ctrAnomalies);
  insights.featuredSnippets = limitResults(insights.featuredSnippets);
  insights.cannibalization = limitResults(insights.cannibalization);
  insights.longTail = limitResults(insights.longTail);
  insights.localIntent = limitResults(insights.localIntent);

  insights.total = 
    insights.quickWins.length +
    insights.strikingDistance.length +
    insights.ctrAnomalies.length +
    insights.featuredSnippets.length +
    insights.cannibalization.length +
    insights.longTail.length +
    insights.localIntent.length;

  return insights;
};